import { ordinal, type SubmissionView } from '@mogzo/shared';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useProject } from '../components/ProjectLayout';
import { LiveNow, Waiting } from '../components/Publishing';
import {
  checkName,
  checksSummary,
  Mark,
  STATUS_HELP,
  StatusChip,
  took,
  versionState,
  when,
} from '../components/submissions';
import { Alert, Button, buttonClass, cardClass, ErrorNotice, Loading } from '../components/ui';
import { ApiError, apiSend, type Project } from '../lib/api';
import { useApi } from '../lib/use-api';

interface SubmissionsList {
  submissions: SubmissionView[];
  listingMissing: string[];
}

const hint = 'text-[13px] text-muted';

/** Takes a version out of checking or review, after asking. */
export async function withdraw(submission: SubmissionView): Promise<string | null> {
  if (
    !window.confirm(
      `Withdraw version ${submission.version}? It leaves the queue; sending it again puts it at the back.`,
    )
  ) {
    return null;
  }
  try {
    await apiSend('POST', `/v1/dev/submissions/${submission.id}/withdraw`);
    return 'withdrawn';
  } catch (error) {
    return error instanceof ApiError ? error.message : 'Couldn’t withdraw it. Try again.';
  }
}

function Step({
  state,
  title,
  detail,
}: {
  state: 'done' | 'now' | 'later';
  title: string;
  detail: string;
}) {
  return (
    <li
      className={`flex min-w-[120px] flex-[1_1_0] flex-col gap-1 border-t-4 pt-3 ${
        state === 'done' ? 'border-ink' : state === 'now' ? 'border-brand' : 'border-line'
      }`}
    >
      <strong className={`text-sm ${state === 'later' ? 'text-muted' : ''}`}>{title}</strong>
      <span className={hint}>{detail}</span>
      <span className="sr-only">
        {state === 'done' ? '(done)' : state === 'now' ? '(now)' : '(to come)'}
      </span>
    </li>
  );
}

/** The version being checked or in review, with where it's got to. */
function Current({
  project,
  submission,
  onChange,
}: {
  project: Project;
  submission: SubmissionView;
  onChange: (message: string | null) => void;
}) {
  const headingId = useId();
  const checking = submission.status === 'checking';
  const summary = checksSummary(submission.checks);
  const listingOnly = submission.kind === 'listing';
  return (
    <section aria-labelledby={headingId} className={`${cardClass} flex flex-col gap-[18px] p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className={`${hint} font-semibold`}>
            {checking ? 'Being checked now' : 'In review now'}
          </p>
          <h3 id={headingId} className="mt-1 text-[22px] font-semibold">
            {listingOnly ? 'Store listing change' : `Version ${submission.version}`}{' '}
            <span className="font-medium text-muted">
              {listingOnly
                ? `· game stays on ${submission.version}`
                : `· build #${submission.buildNumber}`}
            </span>
          </h3>
          <p className={`${hint} mt-1`}>
            Sent {when(submission.createdAt).replace(/^Today, /, 'today at ')}
            {submission.sentFrom ? ` from ${submission.sentFrom}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/projects/${project.id}/submissions/${submission.id}`}
            className={buttonClass('secondary')}
          >
            Details
          </Link>
          <Button
            variant="secondary"
            onClick={async () => {
              const result = await withdraw(submission);
              if (result) onChange(result === 'withdrawn' ? null : result);
            }}
          >
            Withdraw
          </Button>
        </div>
      </div>
      <ol aria-label="Progress" className="flex flex-wrap gap-3">
        <Step state="done" title="Sent" detail={when(submission.createdAt)} />
        {!listingOnly && (
          <Step
            state={checking ? 'now' : 'done'}
            title={checking ? 'Checking' : 'Checks passed'}
            detail={
              checking
                ? 'A few minutes'
                : [summary, took(submission.checksStartedAt, submission.checkedAt)]
                    .filter(Boolean)
                    .join(', ')
            }
          />
        )}
        <Step
          state={checking ? 'later' : 'now'}
          title="In review"
          detail={
            submission.queuePosition
              ? `${ordinal(submission.queuePosition)} in the queue`
              : 'Next, if checks pass'
          }
        />
        <Step state="later" title="Decision" detail="We’ll email you" />
      </ol>
      <p className={`${hint} rounded-[6px] bg-field px-3.5 py-2.5`}>
        One version can be in review at a time. To change something now, withdraw it and send a new
        one; it goes to the back of the queue.
      </p>
    </section>
  );
}

/** The first thing to fix in a version that failed its checks, shown in the history. */
function FirstProblem({ submission }: { submission: SubmissionView }) {
  const check = submission.checks.find((c) => c.outcome === 'failed');
  const finding = check?.findings.find((f) => f.level === 'fail');
  if (!check || !finding) return null;
  return (
    <div className="mt-2 flex flex-col gap-1 rounded-[10px] border border-transparent bg-danger-soft px-3 py-2.5">
      <strong className="text-[13px]">{checkName(check.id)}</strong>
      <span className="text-[13px]">
        {finding.file && (
          <span className="font-mono">
            {finding.file}
            {finding.line ? ` line ${finding.line}` : ''}:{' '}
          </span>
        )}
        {finding.message}
      </span>
    </div>
  );
}

function SubmissionsPage({
  project,
  onProjectChanged,
}: {
  project: Project;
  onProjectChanged: () => void;
}) {
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const list = useApi<SubmissionsList>(`/v1/dev/projects/${project.id}/submissions`, reload);
  const refresh = useCallback(() => setReload((n) => n + 1), []);
  const checking =
    list.status === 'ready' && list.data.submissions.some((s) => s.status === 'checking');

  // Checks take minutes: look again while they run.
  useEffect(() => {
    if (!checking) return;
    const timer = setInterval(() => setReload((n) => n + 1), 5000);
    return () => clearInterval(timer);
  }, [checking]);

  // A version went live (or the live one changed): the header's status follows.
  const liveVersion =
    list.status === 'ready'
      ? (list.data.submissions.find((s) => s.release === 'live')?.version ?? null)
      : project.liveVersion;
  const asked = useRef(project.liveVersion);
  useEffect(() => {
    if (liveVersion === project.liveVersion || asked.current === liveVersion) return;
    asked.current = liveVersion;
    onProjectChanged();
  }, [liveVersion, project.liveVersion, onProjectChanged]);

  if (list.status === 'loading') return <Loading />;
  if (list.status === 'error') return <ErrorNotice error={list.error} />;
  const { submissions, listingMissing } = list.data;
  const current = submissions.find((s) => s.status === 'checking' || s.status === 'in_review');
  // Newest first, so the first approved one not yet live is the one that can go live.
  const waiting = submissions.find((s) => s.status === 'approved' && !s.release);
  const live = submissions.find((s) => s.release === 'live');

  return (
    <>
      <div>
        <h2 className="text-[22px] font-semibold">Submissions</h2>
        <p className="mt-1 max-w-[72ch] text-muted">
          Each submission is one build, its source and your store listing as they were when you sent
          them. MOGZO checks it first, then a reviewer plays it. Your test community keeps running
          whatever happens here.
        </p>
      </div>
      {notice && <Alert tone="error">{notice}</Alert>}

      <div className="flex flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-5">
          {current && (
            <Current
              project={project}
              submission={current}
              onChange={(message) => {
                setNotice(message);
                setReload((n) => n + 1);
              }}
            />
          )}
          {waiting && (
            <Waiting project={project} submission={waiting} live={live} onChange={refresh} />
          )}
          {live && <LiveNow project={project} submission={live} />}
          <section aria-labelledby="history-heading" className={`${cardClass} overflow-hidden`}>
            <h3 id="history-heading" className="px-5 py-[18px] text-[15px] font-semibold">
              All versions
            </h3>
            {submissions.length ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-field text-left text-xs font-semibold text-muted">
                      <th className="px-4 py-2.5">Version</th>
                      <th className="px-4 py-2.5">Sent</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5">
                        <span className="sr-only">Open</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission) => (
                      <tr key={submission.id} className="border-t border-line align-top">
                        <td className="px-4 py-3.5">
                          {submission.kind === 'listing' ? (
                            <>
                              <strong>Store listing</strong>{' '}
                              <span className={hint}>· listing only, on {submission.version}</span>
                            </>
                          ) : (
                            <>
                              <strong>{submission.version}</strong>{' '}
                              <span className={hint}>· build #{submission.buildNumber}</span>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {when(submission.createdAt)}
                        </td>
                        <td className="px-4 py-3.5">
                          <StatusChip status={versionState(submission)} />
                          {submission.release === 'live' && submission.publishedAt && (
                            <p className={`${hint} mt-1.5`}>Since {when(submission.publishedAt)}</p>
                          )}
                          {versionState(submission) === 'scheduled' && submission.publishAt && (
                            <p className={`${hint} mt-1.5`}>
                              {when(submission.publishAt, { at: true })}
                            </p>
                          )}
                          {submission.status === 'failed_checks' && (
                            <FirstProblem submission={submission} />
                          )}
                          {submission.status === 'in_review' &&
                            submission.checks.some((c) => c.outcome === 'warnings') && (
                              <p className={`${hint} mt-1.5`}>{checksSummary(submission.checks)}</p>
                            )}
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <Link
                            to={`/projects/${project.id}/submissions/${submission.id}`}
                            className="font-semibold"
                            aria-label={
                              submission.kind === 'listing'
                                ? `Details of the store listing change sent ${when(submission.createdAt)}`
                                : `Details of version ${submission.version}`
                            }
                          >
                            Details
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="border-t border-line px-5 py-6 text-muted">
                Nothing sent yet. When the game and its store listing are ready, send it with{' '}
                <span className="font-mono text-[13px]">mogzo submit</span>.
              </p>
            )}
          </section>
        </div>

        <aside className="flex max-w-[360px] min-w-0 flex-[1_1_280px] flex-col gap-4">
          <section
            aria-labelledby="send-heading"
            className={`${cardClass} flex flex-col gap-3 p-5`}
          >
            <h2 id="send-heading" className="text-[15px] font-semibold">
              Send a version
            </h2>
            <p className={hint}>From your game’s folder:</p>
            <pre className="rounded-[6px] border border-line bg-field px-3.5 py-3 font-mono text-[13px] whitespace-pre-wrap">
              mogzo submit
            </pre>
            <p className={hint}>
              It sends your newest build (or <span className="font-mono">--build 12</span>) with a
              zip of your source, leaving out <span className="font-mono">node_modules</span>,{' '}
              <span className="font-mono">.env</span> and anything in{' '}
              <span className="font-mono">.mogzoignore</span>. Only reviewers see the source.
            </p>
            <div className="flex items-start gap-2.5 rounded-[6px] bg-field px-3 py-2.5">
              <Mark kind={listingMissing.length ? 'fail' : 'passed'} />
              <span className="min-w-0 text-sm font-semibold">
                {listingMissing.length ? (
                  <>
                    Store listing isn’t ready
                    <span className={`${hint} block font-normal`}>
                      Still needed: {listingMissing.map((item) => item.toLowerCase()).join(', ')}
                    </span>
                  </>
                ) : (
                  'Store listing is ready'
                )}
              </span>
              <Link
                to={`/projects/${project.id}/listing`}
                className="ml-auto text-[13px] font-semibold"
              >
                Edit
              </Link>
            </div>
          </section>

          <section
            aria-labelledby="statuses-heading"
            className={`${cardClass} flex flex-col gap-2.5 p-5`}
          >
            <h2 id="statuses-heading" className="text-[15px] font-semibold">
              What the statuses mean
            </h2>
            <dl className="flex flex-col gap-2.5 text-[13px]">
              {STATUS_HELP.map(([status, meaning]) => (
                <div key={status}>
                  <dt>
                    <StatusChip status={status} />
                  </dt>
                  <dd className="mt-1 text-muted">{meaning}</dd>
                </div>
              ))}
            </dl>
          </section>
        </aside>
      </div>
    </>
  );
}

/** The Submissions tab (M21): versions and listing changes sent for review. */
export function ProjectSubmissions() {
  const { project, reload } = useProject();
  return <SubmissionsPage project={project} onProjectChanged={reload} />;
}
